import { userLogout } from '@redux/authent/authent.reducer';
import { createEntityAdapter, createSlice, current, PayloadAction, Update } from '@reduxjs/toolkit';
import { isStatisticsMap } from '@utils/map/map.utils';
import { getNewMenuNavigationByClosedPopupType, haveSamePopupType, isSamePopupType } from '@utils/popup.utils';
import { toLonLatArray } from '@utils/validation/coordinates.utils';
import { Polygon } from 'geojson';

import { zoneCategoryToTypeFormMapper, ZoneFormTypeEnum, ZoneToEditType } from '@/types/config/config.types';
import { NotificationCriteria } from '@/types/data/data.types';
import { MapIdEnum } from '@/types/map.types';
import { PopupDataTypeValues, PopupType, PopupTypeEnum } from '@/types/popup.types';

export type DisplayedPerimeterLinksKeys =
  | {
      automatonUniqueKey: string;
      cabinetUniqueKey?: never;
      sectionUniqueKey?: never;
    }
  | {
      automatonUniqueKey: string;
      cabinetUniqueKey: string;
      sectionUniqueKey?: never;
    }
  | {
      automatonUniqueKey: string;
      cabinetUniqueKey: string;
      sectionUniqueKey: string;
    };

export type BaseMapState = {
  mapId: string;
  popups: PopupType[];
  displayedPerimeterLinksKeys: DisplayedPerimeterLinksKeys | null;
  menuNavigation: string[];
  drawInfo: {
    zoneType: ZoneFormTypeEnum | undefined;
    polygonPoints: Polygon | undefined;
    isDrawnOnto: boolean;
    editedZone: ZoneToEditType | undefined;
  };
};
export type StatisticsMapState = BaseMapState & { criteria: NotificationCriteria | null };
export type MapStateType = BaseMapState | StatisticsMapState;

export const LIVE: BaseMapState = {
  mapId: MapIdEnum.LIVE,
  popups: [],
  displayedPerimeterLinksKeys: null,
  menuNavigation: [],
  drawInfo: {
    zoneType: undefined,
    polygonPoints: undefined,
    isDrawnOnto: false,
    editedZone: undefined,
  },
};

export const REPLAY: BaseMapState = {
  mapId: MapIdEnum.REPLAY,
  popups: [],
  displayedPerimeterLinksKeys: null,
  menuNavigation: [],
  drawInfo: {
    zoneType: undefined,
    polygonPoints: undefined,
    isDrawnOnto: false,
    editedZone: undefined,
  },
};

export const LAD: BaseMapState = {
  mapId: MapIdEnum.LAD,
  popups: [],
  displayedPerimeterLinksKeys: null,
  menuNavigation: [],
  drawInfo: {
    zoneType: undefined,
    polygonPoints: undefined,
    isDrawnOnto: false,
    editedZone: undefined,
  },
};

export const STATISTICS: StatisticsMapState = {
  mapId: MapIdEnum.STATISTICS,
  popups: [],
  displayedPerimeterLinksKeys: null,
  menuNavigation: [],
  drawInfo: {
    zoneType: undefined,
    polygonPoints: undefined,
    isDrawnOnto: false,
    editedZone: undefined,
  },
  criteria: null,
};

type MapState = {
  ids: string[];
  entities: {
    [LIVE.mapId]: BaseMapState;
    [REPLAY.mapId]: BaseMapState;
    [LAD.mapId]: BaseMapState;
    [STATISTICS.mapId]: StatisticsMapState;
  };
};

export const MAP_INITIAL_STATE: MapState = {
  ids: [LIVE.mapId, REPLAY.mapId, LAD.mapId, STATISTICS.mapId],
  entities: {
    [LIVE.mapId]: LIVE,
    [REPLAY.mapId]: REPLAY,
    [LAD.mapId]: LAD,
    [STATISTICS.mapId]: STATISTICS,
  },
};

export const mapsAdapter = createEntityAdapter<MapStateType, string>({
  selectId: (map) => map.mapId,
  sortComparer: (a, b) => a.mapId.localeCompare(b.mapId),
});

const commonUpdatePopupByMapId = (state: MapState, action: PayloadAction<{ mapId: MapIdEnum; popup: PopupType }>) => {
  const { mapId, popup } = action.payload;
  const newPopups: PopupType[] = current(state).entities[mapId].popups.filter(
    (p: PopupType) => !haveSamePopupType(p, popup),
  );
  newPopups.push(popup);
  mapsAdapter.updateOne(state, {
    id: mapId,
    changes: { popups: newPopups },
  });
};

const maps = createSlice({
  name: 'maps',
  initialState: MAP_INITIAL_STATE,
  reducers: {
    addMap: (state, action: PayloadAction<MapStateType>) => mapsAdapter.addOne(state, action.payload),
    addMany: (state, action: PayloadAction<MapStateType[]>) => mapsAdapter.addMany(state, action.payload),
    updateMap: (state, action: PayloadAction<Update<MapStateType, string>>) =>
      mapsAdapter.updateOne(state, action.payload),
    upsertMap: (state, action: PayloadAction<MapStateType>) => mapsAdapter.upsertOne(state, action.payload),
    deleteMap: (state, action: PayloadAction<string>) => mapsAdapter.removeOne(state, action.payload),
    updatePopupByMapId: (state, action: PayloadAction<{ mapId: MapIdEnum; popup: PopupType }>) => {
      commonUpdatePopupByMapId(state, action);
    },
    updatePopupByMapIdFromCockpit: (state, action: PayloadAction<{ mapId: MapIdEnum; popup: PopupType }>) => {
      commonUpdatePopupByMapId(state, action); // Intentional duplicate to differentiate propagation behaviour in map store
    },
    closePopupByMapIdAndType: (
      state,
      action: PayloadAction<{
        mapId: MapIdEnum;
        type: PopupTypeEnum;
        dataType?: PopupDataTypeValues | null;
      }>,
    ) => {
      const { mapId, type, dataType } = action.payload;
      const newPopups: PopupType[] = current(state).entities[mapId].popups.filter(
        (popup) => !isSamePopupType(popup, type, dataType),
      );
      mapsAdapter.updateOne(state, {
        id: mapId,
        changes: { popups: newPopups },
      });
    },
    closeLastOpenedPopupByMapId: (state, action: PayloadAction<MapIdEnum>) => {
      const mapId = action.payload;
      const newPopups: PopupType[] = structuredClone(current(state).entities[mapId].popups);
      const closedPopup = newPopups.pop();
      const currentMenuNavigation = current(state).entities[mapId].menuNavigation;
      let newMenuNavigation;
      const currentDrawInfo = current(state).entities[mapId].drawInfo;
      let newDrawInfo;

      if (closedPopup) {
        newMenuNavigation = getNewMenuNavigationByClosedPopupType(
          closedPopup.type as PopupTypeEnum,
          currentMenuNavigation,
        );

        if (closedPopup.type === PopupTypeEnum.ZONE) {
          newDrawInfo = {
            zoneType: undefined,
            polygonPoints: undefined,
            isDrawnOnto: false,
            editedZone: undefined,
          };
        }
      }
      mapsAdapter.updateOne(state, {
        id: mapId,
        changes: {
          popups: newPopups,
          menuNavigation: newMenuNavigation ?? currentMenuNavigation,
          drawInfo: newDrawInfo ?? currentDrawInfo,
        },
      });
    },
    closeAllPopupsByMapId: (state, action: PayloadAction<MapIdEnum>) => {
      mapsAdapter.updateOne(state, {
        id: action.payload,
        changes: { popups: [] },
      });
    },
    updateDisplayedPerimeterLinksKeysByMapId: (
      state,
      action: PayloadAction<{
        mapId: MapIdEnum;
        displayedPerimeterLinksKeys: DisplayedPerimeterLinksKeys | null;
      }>,
    ) => {
      const { mapId, displayedPerimeterLinksKeys } = action.payload;
      mapsAdapter.updateOne(state, {
        id: mapId,
        changes: { displayedPerimeterLinksKeys },
      });
    },
    hidePerimeterLinksByMapId: (state, action: PayloadAction<MapIdEnum>) => {
      mapsAdapter.updateOne(state, {
        id: action.payload,
        changes: { displayedPerimeterLinksKeys: null },
      });
    },
    switchDrawModeByMapId: (state, action: PayloadAction<{ mapId: MapIdEnum; isDrawable?: boolean }>) => {
      const { mapId, isDrawable } = action.payload;
      mapsAdapter.updateOne(state, {
        id: mapId,
        changes: {
          drawInfo: {
            zoneType: undefined,
            polygonPoints: undefined,
            isDrawnOnto: isDrawable ?? !current(state).entities[mapId].drawInfo.isDrawnOnto,
            editedZone: undefined,
          },
        },
      });
    },
    updateDrawZoneTypeSelected: (
      state,
      action: PayloadAction<{ mapId: MapIdEnum; zoneType: ZoneFormTypeEnum | undefined }>,
    ) => {
      const { mapId, zoneType } = action.payload;
      mapsAdapter.updateOne(state, {
        id: mapId,
        changes: {
          drawInfo: {
            ...state.entities[mapId].drawInfo,
            zoneType: zoneType,
          },
        },
      });
    },
    updateDrawPolygonsData: (state, action: PayloadAction<{ mapId: MapIdEnum; points: Polygon }>) => {
      const { mapId, points } = action.payload;
      mapsAdapter.updateOne(state, {
        id: mapId,
        changes: {
          drawInfo: {
            ...state.entities[mapId].drawInfo,
            polygonPoints: points,
          },
        },
      });
    },
    updateEditPolygonsData: (
      state,
      action: PayloadAction<{
        mapId: MapIdEnum;
        zone: ZoneToEditType;
      }>,
    ) => {
      const { mapId, zone } = action.payload;
      mapsAdapter.updateOne(state, {
        id: mapId,
        changes: {
          drawInfo: {
            ...state.entities[mapId].drawInfo,
            polygonPoints:
              'type' in zone.polygon
                ? zone.polygon
                : { type: 'Polygon', coordinates: [zone.polygon.map((value) => toLonLatArray(value, false)!)] },
            editedZone: zone,
            zoneType: zoneCategoryToTypeFormMapper(zone.type, 'level' in zone ? zone.level : undefined),
          },
        },
      });
    },
    updateMenuNavigationStatus: (state, action: PayloadAction<{ mapId: MapIdEnum; selectedOptions: string[] }>) => {
      const { mapId, selectedOptions } = action.payload;
      mapsAdapter.updateOne(state, {
        id: mapId,
        changes: {
          menuNavigation: selectedOptions,
        },
      });
    },
    updateStatisticsCriteria: (state, action: PayloadAction<NotificationCriteria>) => {
      const map = state.entities[STATISTICS.mapId];
      if (isStatisticsMap(map)) {
        (map as StatisticsMapState).criteria = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(userLogout, () => MAP_INITIAL_STATE);
  },
});

export const {
  addMap,
  addMany,
  updateMap,
  upsertMap,
  deleteMap,
  updatePopupByMapId,
  updatePopupByMapIdFromCockpit,
  closePopupByMapIdAndType,
  closeLastOpenedPopupByMapId,
  closeAllPopupsByMapId,
  updateDisplayedPerimeterLinksKeysByMapId,
  hidePerimeterLinksByMapId,
  switchDrawModeByMapId,
  updateDrawZoneTypeSelected,
  updateDrawPolygonsData,
  updateEditPolygonsData,
  updateMenuNavigationStatus,
  updateStatisticsCriteria,
} = maps.actions;

export default maps.reducer;
